import React from "react";
import MobileRecordCard from "./MobileRecordCard.jsx";
import DataManagerProductSelect from "./DataManagerProductSelect.jsx";
import BillToSelect from "./BillToSelect.jsx";
import { Ellipsis, Eye, LoaderCircle, LockKeyhole, Moon, RotateCcw, Save, ShieldCheck, Sun, Truck, X } from "lucide-react";
import DataManagerOrdersSearchHighlight from "./DataManagerOrdersSearchHighlight.jsx";
import {
  buildDataManagerGridRowClassName,
  DATA_MANAGER_GRID_BADGE_RADIUS_CLASS_NAME,
  handleDataManagerGridRowClick,
} from "../utils/dataManagerGridRowStyles.js";
import {
  buildFrozenColumnCellStyle,
  DATA_MANAGER_FROZEN_LAST_COLUMN_CLASS_NAME,
  ORDER_GRID_SELECTION_COLUMN_KEY,
  resolveFrozenOrderCellBackgroundClassName,
} from "../utils/dataManagerFrozenPanes.js";
import {
  ASPIRATOR_SELECT_OPTIONS,
  ORDER_ACTIONS_COLUMN,
  ORDER_BILLING_APPROVAL_COLUMN,
  ORDER_DELIVERY_SCOPE_TITLE,
  ORDER_DISPATCH_COLUMN,
  ORDER_GRID_SELECTION_COLUMN_WIDTH,
  buildOrderGridColumnStyle,
  ORDER_LOADING_PRIORITY_OPTIONS,
  ORDER_SEARCH_HIGHLIGHT_COLUMN_KEYS,
  PRELOADED_PREVIOUS_DAY_SELECT_OPTIONS,
  canEditSingleDeliveryWindowFields,
  coerceLoadingClientDraftValue,
  formatDateDisplay,
  formatOrderActionDisplay,
  formatQuantityTons,
  formatTonneRate,
  formatTextDisplay,
  formatWindowDisplay,
  hasDraftErrors,
  hasOrderDraftChanges,
  resolveOrderSaveDisabledReason,
  isDeliveryWindowField,
  isOrderAddressField,
  isOrderAddressFocusKey,
  normalizeAspiratorDraftValue,
  normalizeOntarioDraftValue,
  normalizeLoadingPriorityDraftValue,
  normalizeDeliveryPeriodDraftValue,
  normalizePreloadedPreviousDayDraftValue,
  resolveBillingApprovalMeta,
  resolveDispatchStatusMeta,
  resolveDeliverySummary,
  resolveEndOfDayLoadHoverCardDetails,
  resolveLoadingAddressPreview,
  resolveLoadingSummary,
  resolveLockMeta,
  resolveEndOfDayLoadDisplayLabel,
  resolveEndOfDayLoadTitle,
  resolveOrderPrimaryAction,
  resolveOrderTimeFieldValue,
  resolveReadonlyTextCell,
  isOrderEndOfDayLoadLocked,
} from "../hooks/useDataManagerOrdersUtils.js";
import { resolveOrderGridCellFocusKey } from "../utils/dataManagerOrderGridFocus.js";
import {
  EndOfDayLoadDeliveryDateHoverCard,
  EndOfDayLoadHoverCard,
  LoadingAddressHoverCard,
  LoadingClientAutocompleteInput,
  PickerInput,
  handleEditableGridKeyDown,
  preventAddressInputDragEvent,
  selectDateInputDaySegment,
} from "./DataManagerOrdersPrimitives.jsx";

const PRIMARY_ACTION_CLASS_NAME = "inline-flex h-8 items-center gap-1 rounded-full bg-accent px-2.75 text-[11px] font-semibold text-white transition-colors duration-150 hover:bg-accent-strong disabled:cursor-not-allowed disabled:opacity-60";
const SECONDARY_ACTION_CLASS_NAME = "inline-flex h-8 items-center gap-1 rounded-full border border-slate-200 bg-white px-2.75 text-[11px] font-semibold text-slate-700 transition-colors duration-150 hover:border-slate-300 hover:text-slate-950 disabled:cursor-not-allowed disabled:opacity-60";
const ROW_PRIMARY_ACTION_CLASS_NAME = "inline-flex min-h-7 max-w-full items-center gap-1 whitespace-normal rounded-[4px] border border-accent bg-accent px-2 py-1 text-[10px] font-semibold text-white hover:border-accent-strong hover:bg-accent-strong focus-visible:outline-accent disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400 disabled:opacity-100 disabled:shadow-none";
const ROW_SECONDARY_ACTION_CLASS_NAME = "inline-flex h-7 max-w-full items-center gap-1 rounded-full border border-slate-200 bg-white px-2 text-[10px] font-semibold text-slate-700 transition-colors duration-150 hover:border-slate-300 hover:text-slate-950 disabled:cursor-not-allowed disabled:opacity-60";
const INPUT_ERROR_CLASS_NAME = "border-rose-300 bg-rose-50/60 text-rose-900 placeholder:text-rose-300 focus:border-rose-400";
const GRID_CELL_CLASS_NAME = "border-b border-slate-100 px-2 py-1.5 align-middle";
const GRID_INPUT_CLASS_NAME = "h-7 w-full min-w-0 rounded-[8px] border border-slate-200 bg-white px-2 text-[11px] text-slate-700 outline-hidden transition-colors duration-150 placeholder:text-slate-400 focus:border-accent disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500";
const GRID_NUMBER_INPUT_CLASS_NAME = `${GRID_INPUT_CLASS_NAME} min-w-[4.75rem]`;
const GRID_TIME_INPUT_CLASS_NAME = `${GRID_INPUT_CLASS_NAME} min-w-[6.25rem] max-w-none`;
const GRID_PICKER_INPUT_CLASS_NAME = "h-7 w-full min-w-0 appearance-none rounded-[8px] border border-slate-200 bg-white pl-2 pr-7 text-[11px] text-slate-700 outline-hidden transition-colors duration-150 focus:relative focus:z-20 focus:w-[10.5rem] focus:max-w-none focus:border-accent focus:shadow-[0_12px_28px_rgba(15,23,42,0.16)] disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500 [&::-webkit-calendar-picker-indicator]:pointer-events-none [&::-webkit-calendar-picker-indicator]:opacity-0";
const GRID_PICKER_BUTTON_CLASS_NAME = "absolute inset-y-0 right-0 flex w-7 items-center justify-center rounded-r-[8px] border-l border-slate-200 bg-slate-50 text-slate-500 transition-colors duration-150 hover:text-slate-700 disabled:cursor-not-allowed disabled:text-slate-300";
const GRID_VALUE_CLASS_NAME = "block max-w-full truncate text-[10.5px] leading-4 text-slate-700";
const END_OF_DAY_LOAD_LOCKED_DATE_VALUE_CLASS_NAME = "inline-flex h-7 w-full min-w-0 items-center rounded-[8px] border border-amber-200 bg-amber-50 px-2 text-[10.5px] font-semibold leading-4 text-amber-900";
const END_OF_DAY_LOAD_LOCKED_DATE_INPUT_CLASS_NAME = "!border-amber-200 !bg-amber-50 font-semibold !text-amber-900 disabled:!border-amber-200 disabled:!bg-amber-50 disabled:!text-amber-900";
const END_OF_DAY_LOAD_LOCKED_DATE_PICKER_BUTTON_CLASS_NAME = "!border-amber-200 !bg-amber-100 !text-amber-800 disabled:!text-amber-800";
const EXPANDED_ADDRESS_INPUT_CLASS_NAME = "focus:relative focus:z-20 focus:w-[24rem] focus:max-w-none focus:shadow-[0_12px_28px_rgba(15,23,42,0.16)]";
function getAddressEditInputClassName(baseClassName, isAddressField = false) {
  return isAddressField ? `${baseClassName} ${EXPANDED_ADDRESS_INPUT_CLASS_NAME}` : baseClassName;
}

function StatusBadge({ className, label, title = undefined }) {
  return (
    <span
      className={[
        `inline-flex items-center ${DATA_MANAGER_GRID_BADGE_RADIUS_CLASS_NAME} px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.12em] leading-none`,
        className,
      ].join(" ")}
      title={title}
    >
      {label}
    </span>
  );
}

function PreloadedPreviousDayBadge() {
  return (
    <span
      className={`inline-flex h-7 max-w-full items-center gap-1 ${DATA_MANAGER_GRID_BADGE_RADIUS_CLASS_NAME} border border-cyan-200 bg-cyan-50 px-2 text-[10px] font-semibold text-cyan-800`}
      title="Chargé la veille"
    >
      <Moon size={12} aria-hidden="true" />
      <span className="truncate">Veille</span>
    </span>
  );
}

function AspiratorBadge() {
  return (
    <span
      className={`inline-flex items-center ${DATA_MANAGER_GRID_BADGE_RADIUS_CLASS_NAME} border border-green-200 bg-green-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-green-700`}
      title="Aspirateur requis"
    >
      Oui
    </span>
  );
}

function EveningDeliveryBadge() {
  return (
    <span
      className={`inline-flex h-7 max-w-full items-center gap-1 ${DATA_MANAGER_GRID_BADGE_RADIUS_CLASS_NAME} border border-slate-700 bg-slate-950 px-2 text-[10px] font-semibold text-white`}
      title="Livraison en soirée"
    >
      <Moon size={12} aria-hidden="true" />
      <span className="truncate">Soir</span>
    </span>
  );
}

function OntarioBadge() {
  return (
    <span
      className={`inline-flex items-center ${DATA_MANAGER_GRID_BADGE_RADIUS_CLASS_NAME} border border-green-200 bg-green-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-green-700`}
      title="Livraison Ontario"
    >
      Oui
    </span>
  );
}

function DompeurBadge() {
  return (
    <span
      className={`inline-flex h-7 max-w-full items-center gap-1 ${DATA_MANAGER_GRID_BADGE_RADIUS_CLASS_NAME} border border-orange-200 bg-orange-50 px-2 text-[10px] font-semibold text-orange-800`}
      title="Camion dompeur"
    >
      <Truck size={12} aria-hidden="true" />
      <span className="truncate">Domp.</span>
    </span>
  );
}

function resolveOrderOptionBadge(columnKey, order, isEditing) {
  if (isEditing) {
    return null;
  }

  if (columnKey === "is_preloaded_previous_day" && order?.is_preloaded_previous_day === true) {
    return <PreloadedPreviousDayBadge />;
  }

  if (columnKey === "aspirator" && normalizeAspiratorDraftValue(order?.aspirator) === "oui") {
    return <AspiratorBadge />;
  }

  if (columnKey === "delivery_period" && normalizeDeliveryPeriodDraftValue(order?.delivery_period) === "oui") {
    return <EveningDeliveryBadge />;
  }

  if (columnKey === "ontario" && normalizeOntarioDraftValue(order?.ontario) === "oui") {
    return <OntarioBadge />;
  }

  if (columnKey === "is_dompeur" && (order?.is_dompeur === true || normalizePreloadedPreviousDayDraftValue(order?.is_dompeur) === "oui")) {
    return <DompeurBadge />;
  }

  return null;
}

function resolveGridFocusKeyFromEvent(event, fallbackKey) {
  const focusNode = typeof event?.target?.closest === "function"
    ? event.target.closest("[data-grid-focus-key]")
    : null;
  const focusKey = typeof focusNode?.getAttribute === "function"
    ? focusNode.getAttribute("data-grid-focus-key")
    : "";

  return focusKey || fallbackKey;
}

function shouldSelectGridInputOnFocus(inputType = "") {
  return inputType !== "date" && inputType !== "time";
}

function focusGridInput(node) {
  if (!node || typeof node.focus !== "function") {
    return;
  }

  node.focus({ preventScroll: true });
  if (node.type === "date" || node.dataset?.dateSegmentInput === "day-first") {
    selectDateInputDaySegment(node);
    return;
  }
  if (shouldSelectGridInputOnFocus(node.type) && typeof node.select === "function") {
    node.select();
  }
}

function openFocusedGridSelect(node) {
  const tagName = typeof node?.tagName === "string" ? node.tagName.toUpperCase() : "";
  if (tagName !== "SELECT" || typeof node.showPicker !== "function") {
    return;
  }

  try {
    node.focus?.({ preventScroll: true });
    node.showPicker();
  } catch {
    // Some browsers require a live user activation; keep the focus fallback.
  }
}

function useEditableRowFocus(isEditing, focusKey) {
  const rowRef = React.useRef(null);

  React.useLayoutEffect(() => {
    if (!isEditing) {
      return undefined;
    }

    const focusableNodes = Array.from(
      rowRef.current?.querySelectorAll("input[data-grid-focus-key], select[data-grid-focus-key], textarea[data-grid-focus-key]") || []
    ).filter((node) => !node.disabled);
    const preferredNode = focusableNodes.find((node) => node.getAttribute("data-grid-focus-key") === focusKey);
    const fallbackNode = focusableNodes[0] || null;
    const targetNode = preferredNode || fallbackNode;

    if (!targetNode || typeof targetNode.focus !== "function") {
      return undefined;
    }

    focusGridInput(targetNode);
    openFocusedGridSelect(targetNode);

    return undefined;
  }, [focusKey, isEditing]);

  return rowRef;
}

const ORDER_MENU_ACTION_CLASS_NAME = "flex min-h-8 w-full items-center gap-2 whitespace-nowrap rounded-[8px] px-2.5 py-1.5 text-left text-[11px] font-semibold text-slate-700 hover:bg-slate-100 hover:text-slate-950 disabled:cursor-not-allowed disabled:opacity-50";

function OrderSecondaryActionsMenu({
  canClearDeliveredOrder = false,
  canOpenExecution = false,
  disabled = false,
  isEditing = false,
  onCancel = null,
  onClearDeliveredOrder = null,
  onEditExecution = null,
  order = null,
  primaryAction = null,
}) {
  const [isOpen, setIsOpen] = React.useState(false);
  const menuRef = React.useRef(null);
  const hasSecondaryAction = isEditing
    ? Boolean(onCancel)
    : Boolean(
      (canOpenExecution && primaryAction !== "execution")
      || canClearDeliveredOrder
    );

  React.useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    function handlePointerDown(event) {
      if (!menuRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    }

    function handleKeyDown(event) {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    }

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  if (!hasSecondaryAction) {
    return null;
  }

  function runAction(action) {
    setIsOpen(false);
    action?.();
  }

  return (
    <div ref={menuRef} className="relative shrink-0" onClick={(event) => event.stopPropagation()}>
        <button
          type="button"
          aria-expanded={isOpen}
          aria-haspopup="menu"
          aria-label={`Plus d’actions pour la commande ${order?.order_id || ""}`.trim()}
          className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 transition hover:border-slate-300 hover:text-slate-950 disabled:cursor-not-allowed disabled:opacity-50"
          disabled={disabled}
          onClick={() => setIsOpen((currentValue) => !currentValue)}
          title="Actions secondaires"
        >
          <Ellipsis size={14} aria-hidden="true" />
        </button>

        {isOpen ? (
          <div className="absolute right-0 top-full z-40 mt-1 min-w-44 rounded-[10px] border border-slate-200 bg-white p-1 shadow-[0_14px_34px_rgba(15,23,42,0.18)]" role="menu">
            {isEditing && onCancel ? (
              <button type="button" className={ORDER_MENU_ACTION_CLASS_NAME} onClick={() => runAction(onCancel)} role="menuitem">
                <X size={13} aria-hidden="true" />
                Fermer l’édition
              </button>
            ) : null}
            {!isEditing && canOpenExecution && primaryAction !== "execution" ? (
              <button type="button" className={ORDER_MENU_ACTION_CLASS_NAME} onClick={() => runAction(onEditExecution)} role="menuitem">
                <Eye size={13} aria-hidden="true" />
                Tonnages réels
              </button>
            ) : null}
            {!isEditing && canClearDeliveredOrder ? (
              <button type="button" className={ORDER_MENU_ACTION_CLASS_NAME} onClick={() => runAction(onClearDeliveredOrder)} role="menuitem">
                <RotateCcw size={13} aria-hidden="true" />
                Retirer le statut livré
              </button>
            ) : null}
          </div>
        ) : null}
    </div>
  );
}

function OrderRowActions({
  canSave,
  canShowBillingApprovalAction,
  canClearDeliveredOrder,
  canOpenExecution,
  canWriteExecution,
  isEditing,
  isPending,
  onApproveForBilling,
  onCancel,
  onClearDeliveredOrder,
  onEditExecution,
  onSave,
  order,
  saveDisabledReason,
  saveFeedbackMessage,
  saveLabel,
  showBusyState,
}) {
  if (isEditing) {
    return (
      <div className="grid min-w-0 gap-1">
        <div className="flex flex-nowrap items-center gap-1 whitespace-nowrap">
          <button
            type="button"
            onClick={onSave}
            disabled={isPending || !canSave}
            title={saveDisabledReason || undefined}
            className={PRIMARY_ACTION_CLASS_NAME}
          >
            {showBusyState ? <LoaderCircle className="h-3.5 w-3.5 animate-spin" /> : <Save size={13} />}
            {saveLabel}
          </button>
          <OrderSecondaryActionsMenu
            disabled={isPending}
            isEditing
            onCancel={onCancel}
            order={order}
          />
        </div>
        {saveFeedbackMessage ? (
          <span className="max-w-52 text-[10px] leading-4 text-rose-600">{saveFeedbackMessage}</span>
        ) : null}
      </div>
    );
  }

  const primaryAction = resolveOrderPrimaryAction({
    canApproveForBilling: canShowBillingApprovalAction,
    canOpenExecution,
  });
  const hasAnyAction = Boolean(
    primaryAction
    || canClearDeliveredOrder
  );
  if (!hasAnyAction) {
    return <span className="text-[10px] text-slate-400">—</span>;
  }

  return (
    <div className="flex min-w-0 flex-nowrap items-center gap-1 whitespace-nowrap">
      {primaryAction === "approve" ? (
        <button type="button" onClick={onApproveForBilling} disabled={isPending} className={ROW_PRIMARY_ACTION_CLASS_NAME} title="Vérifier dans Consolidation">
          <ShieldCheck size={12} aria-hidden="true" />
          À approuver
        </button>
      ) : null}
      {primaryAction === "execution" ? (
        <button
          type="button"
          onClick={onEditExecution}
          disabled={isPending}
          className={ROW_SECONDARY_ACTION_CLASS_NAME}
          title={canWriteExecution ? "Saisir ou corriger les tonnages réels" : "Consulter les tonnages réels"}
        >
          Tonnages réels
        </button>
      ) : null}
      <OrderSecondaryActionsMenu
        canClearDeliveredOrder={canClearDeliveredOrder}
        canOpenExecution={canOpenExecution}
        disabled={isPending}
        onClearDeliveredOrder={onClearDeliveredOrder}
        onEditExecution={onEditExecution}
        order={order}
        primaryAction={primaryAction}
      />
    </div>
  );
}

function OrderActionBadge({ action }) {
  return (
    <span className={`inline-flex items-center ${DATA_MANAGER_GRID_BADGE_RADIUS_CLASS_NAME} border border-slate-200 bg-slate-100 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.12em] text-slate-700`}>
      {formatOrderActionDisplay(action)}
    </span>
  );
}

function OrderSummaryCell({ primary, primaryFocusKey = undefined, secondary, secondaryFocusKey = undefined, title = undefined }) {
  return (
    <div className="min-w-0">
      <span className="block truncate text-[10.5px] font-medium leading-4 text-slate-700" data-grid-focus-key={primaryFocusKey} title={title}>{primary}</span>
      {secondary && secondary !== "—" ? (
        <span className="mt-0.5 block truncate text-[10px] leading-4 text-slate-500" data-grid-focus-key={secondaryFocusKey} title={title}>{secondary}</span>
      ) : null}
    </div>
  );
}

function OrderDispatchStateCell({ order }) {
  const dispatchMeta = resolveDispatchStatusMeta(order?.dispatch_status);
  const lockMeta = resolveLockMeta(order);
  const showLockIndicator = lockMeta.label !== "Ouverte";

  return (
    <div className="flex min-w-0 items-center gap-1.5">
      <StatusBadge className={dispatchMeta.className} label={dispatchMeta.label} />
      {showLockIndicator ? (
        <span
          aria-label={lockMeta.label}
          className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500"
          title={`${lockMeta.label} · ${lockMeta.title}`}
        >
          <LockKeyhole size={11} aria-hidden="true" />
        </span>
      ) : null}
    </div>
  );
}

function OrderBillingApprovalStateCell({ order }) {
  const approvalMeta = resolveBillingApprovalMeta(order);
  return <StatusBadge className={approvalMeta.className} label={approvalMeta.label} title={approvalMeta.title} />;
}

function renderHighlightedValue(displayValue, columnKey, searchQuery, className, title = undefined) {
  if (!ORDER_SEARCH_HIGHLIGHT_COLUMN_KEYS.has(columnKey)) {
    return <span className={className} title={title}>{displayValue || "—"}</span>;
  }

  return (
    <DataManagerOrdersSearchHighlight
      className={className}
      searchQuery={searchQuery}
      title={title}
    >
      {displayValue || "—"}
    </DataManagerOrdersSearchHighlight>
  );
}

function OrderGridField({
  productCatalog = [],
  clientCorrespondenceOptions = [],
  clientCorrespondenceOptionsByLookupKey = {},
  column,
  draft,
  errors,
  getValidationKeyForField = null,
  isEditing = false,
  isPending = false,
  onCancel = null,
  onChange,
  onSubmit = null,
  order,
  registerAddressValidationFieldRef = null,
  searchQuery = "",
}) {
  const deliveryFieldOutOfScope = isDeliveryWindowField(column.key) && !canEditSingleDeliveryWindowFields(order);
  const isEndOfDayLoadDeliveryDate = column.key === "delivery_date" && isOrderEndOfDayLoadLocked(order);
  const isReadonlyExecutionColumn = column.type === "readonly-number" || column.type === "readonly-delivery-driver";

  if (!isEditing) {
    if (column.type === "readonly-action") {
      return <OrderActionBadge action={order?.action} />;
    }

    if (deliveryFieldOutOfScope) {
      return <span className={GRID_VALUE_CLASS_NAME} title={ORDER_DELIVERY_SCOPE_TITLE}>Multi</span>;
    }

    if (column.type === "readonly-loading-summary") {
      const summary = resolveLoadingSummary(order, clientCorrespondenceOptionsByLookupKey);
      return (
        <LoadingAddressHoverCard address={summary.address}>
          <OrderSummaryCell primary={summary.primary} primaryFocusKey="loading_client_name" title={summary.title} />
        </LoadingAddressHoverCard>
      );
    }

    if (column.type === "readonly-delivery-summary") {
      const summary = resolveDeliverySummary(order);
      return <OrderSummaryCell {...summary} primaryFocusKey="delivery_client_name" secondaryFocusKey="delivery_address" />;
    }

    if (column.type === "date") {
      const dateValue = isEndOfDayLoadDeliveryDate ? (
        <span className={END_OF_DAY_LOAD_LOCKED_DATE_VALUE_CLASS_NAME}>
          <span className="truncate">{formatDateDisplay(order?.[column.key])}</span>
        </span>
      ) : (
        renderHighlightedValue(formatDateDisplay(order?.[column.key]), column.key, searchQuery, GRID_VALUE_CLASS_NAME)
      );
      return isEndOfDayLoadDeliveryDate ? (
        <EndOfDayLoadDeliveryDateHoverCard>{dateValue}</EndOfDayLoadDeliveryDateHoverCard>
      ) : dateValue;
    }

    if (column.type === "time") {
      return <span className={GRID_VALUE_CLASS_NAME}>{formatWindowDisplay(resolveOrderTimeFieldValue(order, column.key))}</span>;
    }

    if (column.type === "number") {
      const displayValue = ["loading_tonne_rate_override", "unloading_tonne_rate_override", "hourly_rate_override", "fixed_rate_override"].includes(column.key)
        ? formatTonneRate(order?.[column.key]).replace("$/t", { hourly_rate_override: "$/h", fixed_rate_override: "$" }[column.key] || "$/t")
        : formatQuantityTons(order?.[column.key]);
      return <span title={column.help}>{renderHighlightedValue(displayValue, column.key, searchQuery, GRID_VALUE_CLASS_NAME)}</span>;
    }

    const { display, title } = resolveReadonlyTextCell(order, column);
    return renderHighlightedValue(display, column.key, searchQuery, GRID_VALUE_CLASS_NAME, title);
  }

  if (column.type === "readonly" || column.type === "readonly-action" || column.type === "readonly-loading-summary" || column.type === "readonly-delivery-summary" || isReadonlyExecutionColumn) {
    if (column.type === "readonly-action") {
      return <OrderActionBadge action={order?.action} />;
    }

    if (column.type === "readonly-loading-summary") {
      const resolvedAddress = resolveLoadingAddressPreview(
        draft.loading_client_name,
        clientCorrespondenceOptionsByLookupKey,
        order?.loading_address || order?.address,
      );

      return (
        <LoadingAddressHoverCard address={resolvedAddress}>
          <div className="relative min-w-0 overflow-visible">
            <LoadingClientAutocompleteInput
              className={errors.loading_client_name ? `${GRID_INPUT_CLASS_NAME} ${INPUT_ERROR_CLASS_NAME}` : GRID_INPUT_CLASS_NAME}
              dataGridFocusKey="loading_client_name"
              disabled={isPending}
              error={errors.loading_client_name}
              listId={`order-loading-client-${order?.order_id || "draft"}`}
              onBlur={() => {
                const resolvedValue = coerceLoadingClientDraftValue(draft.loading_client_name, clientCorrespondenceOptionsByLookupKey);
                if (resolvedValue !== draft.loading_client_name) {
                  onChange("loading_client_name", resolvedValue);
                }
              }}
              onFocus={(event) => event.target.select?.()}
              onKeyDown={(event) => handleEditableGridKeyDown(event, { onCancel, onSubmit })}
              onValueChange={(nextValue) => onChange("loading_client_name", nextValue)}
              options={clientCorrespondenceOptions}
              placeholder="Nom client"
              value={coerceLoadingClientDraftValue(draft.loading_client_name, clientCorrespondenceOptionsByLookupKey)}
            />
          </div>
        </LoadingAddressHoverCard>
      );
    }

    if (column.type === "readonly-delivery-summary") {
      return (
        <div className="relative grid min-w-0 gap-1 overflow-visible">
          <input
            className={GRID_INPUT_CLASS_NAME}
            data-grid-focus-key="delivery_client_name"
            disabled={isPending}
            onChange={(event) => onChange("client_name", event.target.value)}
            onFocus={(event) => event.target.select?.()}
            onKeyDown={(event) => handleEditableGridKeyDown(event, { onCancel, onSubmit })}
            placeholder="Client de déchargement"
            title={errors.client_name || undefined}
            type="text"
            value={draft.client_name}
          />
          <input
            className={getAddressEditInputClassName(GRID_INPUT_CLASS_NAME, true)}
            data-grid-focus-key="delivery_address"
            disabled={isPending}
            draggable={false}
            ref={(node) => registerAddressValidationFieldRef?.(getValidationKeyForField?.("address"), node)}
            onChange={(event) => onChange("address", event.target.value)}
            onDragOver={preventAddressInputDragEvent}
            onDragStart={preventAddressInputDragEvent}
            onDrop={preventAddressInputDragEvent}
            onFocus={(event) => event.target.select?.()}
            onKeyDown={(event) => handleEditableGridKeyDown(event, { onCancel, onSubmit })}
            placeholder="Adresse de déchargement"
            title={errors.address || undefined}
            type="text"
            value={draft.address}
          />
        </div>
      );
    }

    if (isReadonlyExecutionColumn) {
      const { display, title } = resolveReadonlyTextCell(order, column);
      return <span className={`${GRID_VALUE_CLASS_NAME} text-slate-500`} title={title}>{display}</span>;
    }

    return <span className={`${GRID_VALUE_CLASS_NAME} text-slate-500`}>{formatTextDisplay(order?.[column.key])}</span>;
  }

  if (deliveryFieldOutOfScope) {
    return <span className={`${GRID_VALUE_CLASS_NAME} text-slate-500`} title={ORDER_DELIVERY_SCOPE_TITLE}>Multi</span>;
  }

  const fieldError = errors[column.key] || "";
  const hasError = Boolean(fieldError);
  const commonProps = {
    className: getAddressEditInputClassName(
      hasError ? `${GRID_INPUT_CLASS_NAME} ${INPUT_ERROR_CLASS_NAME}` : GRID_INPUT_CLASS_NAME,
      isOrderAddressField(column.key)
    ),
    "data-grid-focus-key": column.key,
    "aria-invalid": hasError || undefined,
    disabled: isPending || isEndOfDayLoadDeliveryDate,
    draggable: isOrderAddressField(column.key) ? false : undefined,
    onChange: (event) => onChange(column.key, event.target.value),
    onDragOver: isOrderAddressField(column.key) ? preventAddressInputDragEvent : undefined,
    onDragStart: isOrderAddressField(column.key) ? preventAddressInputDragEvent : undefined,
    onDrop: isOrderAddressField(column.key) ? preventAddressInputDragEvent : undefined,
    onFocus: (event) => focusGridInput(event.target),
    onKeyDown: (event) => handleEditableGridKeyDown(event, { onCancel, onSubmit }),
    ref: isOrderAddressField(column.key)
      ? (node) => registerAddressValidationFieldRef?.(getValidationKeyForField?.(column.key), node)
      : undefined,
    title: fieldError || undefined,
  };

  if (column.type === "date") {
    const dateInputClassName = isEndOfDayLoadDeliveryDate
      ? `${GRID_PICKER_INPUT_CLASS_NAME} ${END_OF_DAY_LOAD_LOCKED_DATE_INPUT_CLASS_NAME}`
      : hasError
        ? `${GRID_PICKER_INPUT_CLASS_NAME} ${INPUT_ERROR_CLASS_NAME}`
        : GRID_PICKER_INPUT_CLASS_NAME;
    const datePickerButtonClassName = isEndOfDayLoadDeliveryDate
      ? `${GRID_PICKER_BUTTON_CLASS_NAME} ${END_OF_DAY_LOAD_LOCKED_DATE_PICKER_BUTTON_CLASS_NAME}`
      : GRID_PICKER_BUTTON_CLASS_NAME;
    const dateInput = (
      <PickerInput
        {...commonProps}
        buttonClassName={datePickerButtonClassName}
        inputClassName={dateInputClassName}
        selectDayOnFocus
        type="date"
        value={draft[column.key]}
      />
    );
    return isEndOfDayLoadDeliveryDate ? (
      <EndOfDayLoadDeliveryDateHoverCard>{dateInput}</EndOfDayLoadDeliveryDateHoverCard>
    ) : dateInput;
  }

  if (column.type === "time") {
    return (
      <input
        {...commonProps}
        className={hasError ? `${GRID_TIME_INPUT_CLASS_NAME} ${INPUT_ERROR_CLASS_NAME}` : GRID_TIME_INPUT_CLASS_NAME}
        step="60"
        type="time"
        value={draft[column.key]}
      />
    );
  }

  if (column.type === "number") {
    return (
      <input
        {...commonProps}
        className={hasError ? `${GRID_NUMBER_INPUT_CLASS_NAME} ${INPUT_ERROR_CLASS_NAME}` : GRID_NUMBER_INPUT_CLASS_NAME}
        inputMode={String(column.step || "").includes(".") ? "decimal" : "numeric"}
        min={column.min}
        step={column.step}
        aria-label={column.label}
        title={column.help}
        type="number"
        value={draft[column.key]}
      />
    );
  }

  if (column.type === "select") {
    const options = column.key === "loading_priority" || column.key === "delivery_priority"
      ? ORDER_LOADING_PRIORITY_OPTIONS
      : column.key === "is_preloaded_previous_day" || column.key === "delivery_period" || column.key === "is_dompeur" || column.key === "ontario"
        ? PRELOADED_PREVIOUS_DAY_SELECT_OPTIONS
        : ASPIRATOR_SELECT_OPTIONS;
    const value = column.key === "loading_priority"
      || column.key === "delivery_priority"
      ? normalizeLoadingPriorityDraftValue(draft[column.key])
      : column.key === "is_preloaded_previous_day" || column.key === "is_dompeur"
        ? normalizePreloadedPreviousDayDraftValue(draft[column.key])
        : column.key === "delivery_period"
          ? normalizeDeliveryPeriodDraftValue(draft[column.key])
        : column.key === "ontario"
          ? normalizeOntarioDraftValue(draft[column.key])
        : normalizeAspiratorDraftValue(draft[column.key]);

    return (
      <select
        {...commonProps}
        value={value}
      >
        {options.map((option) => (
          <option key={option.value || "empty"} value={option.value}>{option.label}</option>
        ))}
      </select>
    );
  }

  if (column.key === "client_name") {
    return (
      <LoadingClientAutocompleteInput
        className={hasError ? `${GRID_INPUT_CLASS_NAME} ${INPUT_ERROR_CLASS_NAME}` : GRID_INPUT_CLASS_NAME}
        dataGridFocusKey={column.key}
        disabled={isPending}
        error={fieldError}
        listId={`order-delivery-client-${order?.order_id || "draft"}`}
        onFocus={(event) => event.target.select?.()}
        onKeyDown={(event) => handleEditableGridKeyDown(event, { onCancel, onSubmit })}
        onValueChange={(nextValue) => onChange(column.key, nextValue)}
        options={clientCorrespondenceOptions}
        placeholder={column.placeholder}
        value={draft[column.key]}
      />
    );
  }

  if (column.key === "grain_type") {
    return <DataManagerProductSelect {...commonProps} products={productCatalog} value={draft.grain_type}
      originalValue={order?.grain_type ?? ""} onChange={(value) => onChange("grain_type", value)} />;
  }
  if (column.key === "coop") {
    return (
      <BillToSelect
        allowAucun
        ariaLabel={`Facturer à ${order?.order_id || ""}`.trim()}
        className={commonProps.className}
        dataGridFocusKey={column.key}
        disabled={commonProps.disabled}
        onChange={(value) => onChange("coop", value)}
        onKeyDown={commonProps.onKeyDown}
        title={commonProps.title}
        value={draft.coop}
      />
    );
  }
  return (
    <input
      {...commonProps}
      placeholder={column.placeholder}
      type="text"
      value={draft[column.key]}
    />
  );
}

// Numéro donné par la meunerie au chargement : même valeur que dans Consolidation, enregistré à la sortie du champ,
// commande livrée comprise, sans passer par « Modifier ». Figé par un relevé validé, comme les autres valeurs.
function LoadingReferenceCell({ order, editable, onSave }) {
  const saved = order?.loading_reference || "";
  const [value, setValue] = React.useState(saved);
  React.useEffect(() => setValue(saved), [saved]);
  const commit = () => {
    const cleaned = value.trim();
    if (cleaned !== saved) onSave(cleaned).catch(() => setValue(saved));
  };
  return (
    <input
      aria-label={`N° de référence ${order?.order_id || ""}`.trim()}
      className={`${GRID_INPUT_CLASS_NAME} ${value ? "border-transparent bg-transparent hover:border-slate-200" : ""}`}
      disabled={!editable || !onSave}
      maxLength={120}
      placeholder="—"
      title={editable || !order?.lock_reason ? "Numéro donné par la meunerie au chargement, aussi modifiable dans Consolidation" : order.lock_reason}
      type="text"
      value={value}
      onBlur={commit}
      onChange={(event) => setValue(event.target.value)}
      onDoubleClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => {
        event.stopPropagation();
        if (event.key === "Enter") event.currentTarget.blur();
      }}
    />
  );
}

export default function DataManagerOrdersGridRow({
  mobile = false,
  productCatalog = [],
  canOpenConsolidation = false,
  canAssignEndOfDayLoad = false,
  canClearDeliveredOrder = false,
  canEditOrders = false,
  canOpenExecution = false,
  canWriteExecution = false,
  canSelect = false,
  clientCorrespondenceOptions = [],
  clientCorrespondenceOptionsByLookupKey = {},
  columnStyles = {},
  draft,
  editingFocusKey,
  errors,
  frozenColumnLayout = {},
  getValidationKeyForField = null,
  hasDraft,
  isEditing,
  isPending,
  isFocused = false,
  isSelected = false,
  isValidatingAddress = false,
  onAssignEndOfDayLoad,
  onApproveForBilling,
  onCancel,
  onClearDeliveredOrder,
  onEditExecution,
  pinSelectionColumn = false,
  pinnedColumns = [],
  scrollableColumns = [],
  trailingColumnKeySet = new Set(),
  onChange,
  onFocusRow = null,
  onSelectionChange = null,
  onAddressEnterValidation = null,
  onSave,
  onSaveLoadingReference = null,
  onStartEdit,
  order,
  orderDefaultFocusKey = "client_code",
  registerAddressValidationFieldRef = null,
  rowIndex = 0,
  searchQuery = "",
  showSelectionColumn = true,
  validationError = "",
}) {
  const rowRef = useEditableRowFocus(isEditing, editingFocusKey);
  // Une commande livrée ou une journée fermée accepte encore son « Facturer à » : il décide qui paie, pas le dispatch.
  const coopOnly = order?.is_editable === false && order?.can_edit_coop === true;
  // Un relevé ou un préchargement validé fige le « Facturer à », même si le reste de la commande change encore.
  const isFieldEditing = (key) => isEditing && (coopOnly ? key === "coop" : key !== "coop" || order?.can_edit_coop !== false);
  const canEnterEditMode = canEditOrders && (order?.is_editable !== false || coopOnly) && !order?.is_cancelled;
  const canShowBillingApprovalAction = canOpenConsolidation
    && order?.can_approve_for_billing === true
    && String(order?.dispatch_status || "").trim().toLowerCase() === "completed"
    && String(order?.billing_approval_status || "").trim().toLowerCase() === "pending";
  const canShowEndOfDayLoadAction = canAssignEndOfDayLoad && canEnterEditMode && !coopOnly;
  const loadingReferenceCell = <LoadingReferenceCell order={order} editable={canEditOrders && order?.can_edit_loading_reference === true} onSave={onSaveLoadingReference} />;
  const endOfDayLoadDisplayLabel = resolveEndOfDayLoadDisplayLabel(order);
  const endOfDayLoadTitle = resolveEndOfDayLoadTitle(order);
  const endOfDayLoadDetails = resolveEndOfDayLoadHoverCardDetails(order);
  const isEndOfDayLoadEveningMode = endOfDayLoadDetails?.deliveryMode === "evening";
  const EndOfDayLoadModeIcon = isEndOfDayLoadEveningMode ? Moon : Sun;
  const endOfDayLoadModeShortLabel = endOfDayLoadDetails?.deliveryModeShortLabel
    || (isEndOfDayLoadEveningMode ? "Soir" : "Demain");
  const endOfDayLoadButtonClassName = isEndOfDayLoadEveningMode
    ? "inline-flex h-7 max-w-full items-center gap-1 rounded-full border border-slate-700 bg-slate-950 px-2 text-[10px] font-semibold text-white transition-colors duration-150 hover:border-slate-600 hover:bg-slate-900 disabled:cursor-not-allowed disabled:opacity-60"
    : "inline-flex h-7 max-w-full items-center gap-1 rounded-full border border-amber-300 bg-amber-100 px-2 text-[10px] font-semibold text-amber-900 transition-colors duration-150 hover:border-amber-400 hover:bg-amber-50 disabled:cursor-not-allowed disabled:opacity-60";
  const canSave = !hasDraftErrors(errors) && hasOrderDraftChanges(order, draft);
  const saveDisabledReason = canSave
    ? ""
    : resolveOrderSaveDisabledReason({
      draft,
      errors,
      isPending,
      isValidatingAddress,
      order,
    });
  const saveFeedbackMessage = validationError || saveDisabledReason;
  const showBusyState = isPending;
  const saveLabel = isValidatingAddress ? "Validation..." : showBusyState ? "Enregistrement..." : "Enregistrer";

  function resolveFrozenCellProps(columnKey) {
    const layoutEntry = frozenColumnLayout?.[columnKey];
    const baseStyle = columnKey === ORDER_GRID_SELECTION_COLUMN_KEY
      ? buildOrderGridColumnStyle(ORDER_GRID_SELECTION_COLUMN_WIDTH)
      : columnStyles[columnKey];

    if (!layoutEntry?.isFrozen) {
      return {
        className: "",
        style: null,
      };
    }

    return {
      className: [
        resolveFrozenOrderCellBackgroundClassName({
          hasDraft,
          isEditing,
          isFocused,
          rowIndex,
        }),
        layoutEntry.isLastFrozen ? DATA_MANAGER_FROZEN_LAST_COLUMN_CLASS_NAME : "",
      ].filter(Boolean).join(" "),
      style: buildFrozenColumnCellStyle(layoutEntry, baseStyle),
    };
  }

  function renderDataColumnCell(column) {
    const isLockedEndOfDayLoadDateCell = column.key === "delivery_date" && isOrderEndOfDayLoadLocked(order);
    const isExecutionTonnageColumn = column.type === "readonly-number";
    const frozenCellProps = resolveFrozenCellProps(column.key);

    return (
      <td
        key={column.key}
        className={`${GRID_CELL_CLASS_NAME} ${column.widthClassName} ${frozenCellProps.className}`.trim()}
        data-frozen-cell={frozenColumnLayout?.[column.key]?.isFrozen ? "true" : undefined}
        data-grid-focus-key={resolveOrderGridCellFocusKey(column, orderDefaultFocusKey)}
        style={frozenCellProps.style || columnStyles[column.key]}
        title={isExecutionTonnageColumn && canOpenExecution && !isEditing
          ? (canWriteExecution ? "Double-cliquez pour saisir les tonnages réels" : "Double-cliquez pour consulter les tonnages réels")
          : undefined}
        onDoubleClick={isLockedEndOfDayLoadDateCell || isExecutionTonnageColumn
          ? (event) => {
            event.stopPropagation();
            if (isExecutionTonnageColumn && !isEditing && canOpenExecution) {
              onEditExecution?.();
            }
          }
          : undefined}
      >
        {column.key === "end_of_day_load_action" ? (
          !isEditing && canShowEndOfDayLoadAction ? (
            <div className="flex max-w-[9rem] flex-wrap items-center gap-1">
              {endOfDayLoadDisplayLabel ? (
                <EndOfDayLoadHoverCard order={order}>
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      onAssignEndOfDayLoad?.();
                    }}
                    disabled={isPending}
                    aria-label={endOfDayLoadTitle}
                    className={endOfDayLoadButtonClassName}
                  >
                    <EndOfDayLoadModeIcon size={12} aria-hidden="true" />
                    <span className="font-black uppercase tracking-[0.08em]">{endOfDayLoadModeShortLabel}</span>
                    <span className="truncate opacity-85">{endOfDayLoadDisplayLabel}</span>
                  </button>
                </EndOfDayLoadHoverCard>
              ) : (
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    onAssignEndOfDayLoad?.();
                  }}
                  disabled={isPending}
                  className={`${SECONDARY_ACTION_CLASS_NAME} h-7 px-2 text-[10px]`}
                  title={endOfDayLoadTitle}
                >
                  <Sun size={12} aria-hidden="true" />
                  PM
                </button>
              )}
            </div>
          ) : (
            <span className="text-[10px] text-slate-400">—</span>
          )
        ) : column.key === "loading_reference" ? loadingReferenceCell : resolveOrderOptionBadge(column.key, order, isEditing) ?? (
          <OrderGridField
            productCatalog={productCatalog}
            clientCorrespondenceOptions={clientCorrespondenceOptions}
            clientCorrespondenceOptionsByLookupKey={clientCorrespondenceOptionsByLookupKey}
            column={column}
            draft={draft}
            errors={errors}
            getValidationKeyForField={getValidationKeyForField}
            isEditing={isFieldEditing(column.key)}
            isPending={isPending}
            onCancel={onCancel}
            onChange={onChange}
            onSubmit={onSave}
            order={order}
            registerAddressValidationFieldRef={registerAddressValidationFieldRef}
            searchQuery={searchQuery}
          />
        )}
      </td>
    );
  }

  function renderGridColumnCell(column) {
    if (trailingColumnKeySet.has(column.key)) {
      return renderTrailingColumnCell(column);
    }

    return renderDataColumnCell(column);
  }

  function renderTrailingColumnCell(column) {
    const frozenCellProps = resolveFrozenCellProps(column.key);
    const cellClassName = [
      GRID_CELL_CLASS_NAME,
      column.widthClassName || "",
      frozenCellProps.className,
    ].filter(Boolean).join(" ");
    const cellStyle = frozenCellProps.style || columnStyles[column.key];

    return (
      <td
        key={column.key}
        className={cellClassName}
        data-frozen-cell={frozenColumnLayout?.[column.key]?.isFrozen ? "true" : undefined}
        data-grid-focus-key={resolveOrderGridCellFocusKey(column, orderDefaultFocusKey)}
        style={cellStyle}
      >
        {column.key === ORDER_DISPATCH_COLUMN.key ? (
          <OrderDispatchStateCell order={order} />
        ) : null}
        {column.key === ORDER_BILLING_APPROVAL_COLUMN.key ? (
          <OrderBillingApprovalStateCell order={order} />
        ) : null}
      </td>
    );
  }

  if (mobile) {
    const fields = [...pinnedColumns, ...scrollableColumns].filter((column) => !trailingColumnKeySet.has(column.key) && column.key !== "end_of_day_load_action");
    return <MobileRecordCard
      title={`Commande ${order.order_id}`}
      recordId={order.order_id}
      summary={<><span>{order.client_name || order.client_code || "Client non renseigné"}</span><span>{order.grain_type} · {order.quantity_tons ?? "—"} t · {order.delivery_date || "Date non renseignée"}</span><OrderDispatchStateCell order={order} />{(!canEnterEditMode || coopOnly) && order.lock_reason ? <span>{order.lock_reason}</span> : null}</>}
      editing={isEditing} canEdit={canEnterEditMode} pending={isPending} dirty={hasOrderDraftChanges(order, draft)}
      onEdit={() => onStartEdit(coopOnly ? "coop" : orderDefaultFocusKey)} onCancel={onCancel} onSave={onSave} canSave={canSave} feedback={saveFeedbackMessage}
    >
      {fields.map((column) => <label key={column.key} title={column.help}><span>{column.label}</span>
        {column.key === "loading_reference" ? loadingReferenceCell : <OrderGridField productCatalog={productCatalog} clientCorrespondenceOptions={clientCorrespondenceOptions} clientCorrespondenceOptionsByLookupKey={clientCorrespondenceOptionsByLookupKey} column={column} draft={draft} errors={errors} getValidationKeyForField={getValidationKeyForField} isEditing={isFieldEditing(column.key)} isPending={isPending} onChange={onChange} order={order} registerAddressValidationFieldRef={registerAddressValidationFieldRef} searchQuery={searchQuery} />}
        {isEditing && errors?.[column.key] ? <span role="alert">{errors[column.key]}</span> : null}
      </label>)}
    </MobileRecordCard>;
  }

  return (
    <tr
      ref={rowRef}
      data-order-id={order?.order_id || undefined}
      data-frozen-row-state={isEditing ? "highlight" : hasDraft ? "draft" : isFocused ? "highlight" : "normal"}
      data-order-row-editing={isEditing ? "true" : undefined}
      data-order-row-focused={isFocused ? "true" : undefined}
      className={buildDataManagerGridRowClassName({
        isEditing,
        isFocused,
        hasDraft,
        isCancelled: order?.is_cancelled,
        isPending,
        canEnterEditMode,
      })}
      onClick={isEditing ? undefined : () => handleDataManagerGridRowClick(onFocusRow)}
      onDoubleClick={isEditing || !canEnterEditMode ? undefined : (event) => onStartEdit(coopOnly ? "coop" : resolveGridFocusKeyFromEvent(event, orderDefaultFocusKey))}
      onKeyDownCapture={isEditing ? (event) => {
        const focusKey = resolveGridFocusKeyFromEvent(event, "");
        handleEditableGridKeyDown(event, {
          onCancel,
          onSubmit: isOrderAddressFocusKey(focusKey) ? onAddressEnterValidation : onSave,
        });
      } : undefined}
      title={isEditing ? undefined : coopOnly ? `${order.lock_reason || "Commande verrouillée."} Double-cliquez pour corriger le « Facturer à ».`
        : canEnterEditMode ? "Double-cliquez pour éditer cette ligne" : (order?.lock_reason || "Commande en lecture seule")}
    >
      {showSelectionColumn && pinSelectionColumn ? (
        <td
          className={[
            GRID_CELL_CLASS_NAME,
            "w-11 text-center",
            resolveFrozenCellProps(ORDER_GRID_SELECTION_COLUMN_KEY).className,
          ].filter(Boolean).join(" ")}
          style={resolveFrozenCellProps(ORDER_GRID_SELECTION_COLUMN_KEY).style}
          onDoubleClick={(event) => event.stopPropagation()}
        >
          <input
            type="checkbox"
            aria-label={`Sélectionner la commande ${order?.order_id || ""}`.trim()}
            checked={isSelected}
            disabled={!canSelect || isPending}
            onChange={(event) => onSelectionChange?.(event.target.checked)}
            onClick={(event) => {
              event.stopPropagation();
              handleDataManagerGridRowClick(onFocusRow);
            }}
            className="h-3.5 w-3.5 rounded border-slate-300 text-accent focus:ring-accent/30 disabled:cursor-not-allowed disabled:opacity-40"
          />
        </td>
      ) : null}
      {pinnedColumns.map((column) => renderGridColumnCell(column))}
      {showSelectionColumn && !pinSelectionColumn ? (
        <td className={`${GRID_CELL_CLASS_NAME} w-11 text-center`} onDoubleClick={(event) => event.stopPropagation()}>
          <input
            type="checkbox"
            aria-label={`Sélectionner la commande ${order?.order_id || ""}`.trim()}
            checked={isSelected}
            disabled={!canSelect || isPending}
            onChange={(event) => onSelectionChange?.(event.target.checked)}
            onClick={(event) => {
              event.stopPropagation();
              handleDataManagerGridRowClick(onFocusRow);
            }}
            className="h-3.5 w-3.5 rounded border-slate-300 text-accent focus:ring-accent/30 disabled:cursor-not-allowed disabled:opacity-40"
          />
        </td>
      ) : null}
      {scrollableColumns.map((column) => renderGridColumnCell(column))}
      <td
        className={GRID_CELL_CLASS_NAME}
        onDoubleClick={(event) => event.stopPropagation()}
        style={columnStyles[ORDER_ACTIONS_COLUMN.key]}
      >
        <OrderRowActions
          canSave={canSave}
          canShowBillingApprovalAction={canShowBillingApprovalAction}
          canClearDeliveredOrder={canClearDeliveredOrder}
          canOpenExecution={canOpenExecution}
          canWriteExecution={canWriteExecution}
          isEditing={isEditing}
          isPending={isPending}
          onApproveForBilling={onApproveForBilling}
          onCancel={onCancel}
          onClearDeliveredOrder={onClearDeliveredOrder}
          onEditExecution={onEditExecution}
          onSave={onSave}
          order={order}
          saveDisabledReason={saveDisabledReason}
          saveFeedbackMessage={saveFeedbackMessage}
          saveLabel={saveLabel}
          showBusyState={showBusyState}
        />
      </td>
    </tr>
  );
}
